// @flow

// eslint-disable-next-line no-restricted-imports
import { getStore } from '@ta/common.state';
import Cookies from 'js-cookie';
import decodeString from '@ta/common.decode';
import { enqueue } from '@ta/platform.interactions';
import type { EventType } from './unifiedUserTracking';

// eslint-disable-next-line import/no-cycle
export { default as ClickTracker } from './ClickTracker';
// eslint-disable-next-line import/no-cycle
export { default as Track } from './Track';
export { default as LogInteraction } from './LogInteraction.gql';

export { default as trackEventWithContext } from './unifiedUserTracking';
export { default as trackHolisticEvent, EVENTS as holisticEvents } from './holisticTracking';
export type UserTrackingEventType = EventType;

export { default as Impression } from './impressions/Impression';
export type { ImpressionAttributes } from './impressions/ImpressionAttributes';
export {
  TrackingImpressionContext,
  TrackingImpressionContextProvider,
  TrackingImpression,
} from './impressions/TrackingImpressionContext';
export type { TrackingImpressionContextProps } from './impressions/TrackingImpressionContext';

// eslint-disable-next-line import/no-cycle
export { default as useGARecordEvent } from './useGARecordEvent';
// eslint-disable-next-line import/no-cycle
export { default as UseGARecordImpression } from './useGARecordImpression';
export { default as ThirdPartyTracking } from './ThirdPartyTracking';

/**
 * Given a TA URL, look for the given argument -short and long=
 */
export const getArgFromUrl = (url: string, shortArg: string, longArg: string): ?string => {
  const argPattern = new RegExp(`(-${shortArg}([0-9]+))|([?&]${longArg}=([0-9]+))`);
  const matches = argPattern.exec(url);
  if (matches) {
    if (typeof matches[2] !== 'undefined') {
      return matches[2];
    }
    if (typeof matches[4] !== 'undefined') {
      return matches[4];
    }
  }
  return undefined;
};

/**
 * Given a TA URL, return the location (detail if present, geo if not) or null
 */
export const getLocationFromTAUrl = (url: string): ?string =>
  getArgFromUrl(url, 'd', 'detail') || getArgFromUrl(url, 'g', 'geo');

/**
 * Given a TA URL, return the servlet name
 */
export const getServletFromTAUrl = (url: string): ?string => {
  const urlPattern = /(https?:\/\/[^/]+)?\/([^#.?-]*)/;
  const matches = urlPattern.exec(url);
  if (matches) {
    if (typeof matches[2] !== 'undefined') {
      return matches[2] ? matches[2] : 'Home';
    }
  }
  return undefined;
};

/**
 * Implement escaping for encodeDotStar
 */
export const escapeDotStar = (value: any) => {
  let val = value;
  if (typeof val === 'undefined' || val === null) {
    return '';
  }
  if (typeof val !== 'string') {
    val = val.toString();
  }
  return val
    .replace(/!/g, '!!')
    .replace(/\./g, '!.')
    .replace(/\*/g, '!*');
};

/**
 * Encode the attributes of an object into a name value pair using . to
 * delimit value and * to delimit pairs. ! is used as the escape character
 * escape !. !* and !!.
 */
export const encodeDotStar = (obj: { [string]: any }) =>
  Object.keys(obj)
    .map(key => {
      const value = obj[key];
      return `${escapeDotStar(key)}.${escapeDotStar(value)}`;
    })
    .join('*');

/**
 * The flow type for a tracking event to be sent via AJAX. This is the externally visible flow type.
 */
// eslint-disable-next-line flowtype/require-exact-type
export type TrackingEventAJAXProps = {
  /** The tracking module AKA PageProperties */
  module?: string,

  /** The tracking action AKA Page Action */
  action?: string,

  /** The tracking context, AKA Product Attr. */
  context?: string,

  /** The tracking value, AKA Product ID */
  value?: number,

  /** Boolean to indicate if this is or is not due to a user interaction */
  nonInteraction?: boolean,

  /** The tracking source servlet. Pulled from Redux by default unless passed in */
  srcServlet?: string,

  /** The tracking destination servlet */
  dstServlet?: string,

  /** The location (geo or detail). Pulled from Redux by default unless passed in */
  location?: string,

  /** The parent request UID. Pulled from Redux by default unless passed in */
  parentUid?: string,

  /** Boolean for if the member is signed in. Pulled from Redux by default unless passed in */
  isMemberSignedIn?: boolean,

  /** The tracking commerce click location. */
  commerceClickLocation?: string,

  /** The tracking tree string literal */
  trackingTree?: string,

  /** The tracking booking session ID */
  bookingSessionId?: string,
};

/**
 * The flow type for a tracking event to be set as a cookie. This is the externally visible flow type.
 */
export type TrackingEventCookieProps = TrackingEventAJAXProps & {
  /** The tracking destination url. */
  destinationUrl: string,
};

/**
 * The internal flow type for a base GARecord object used to make a /GARecord request. All types are string as we will
 * be sending this over the wire (and because that is the interface for URLSearchParams).
 */
// eslint-disable-next-line flowtype/require-exact-type
type GARecordObj = {
  gac?: string, // module (which /GARecord calls 'category')
  gaa?: string, // action
  gal?: string, // context (which /GARecord calls 'label')
  gav: string, // value
  gani?: string, // non-interaction
  gasl?: string, // source location
  gapu?: string, // parent request UID
  gams: string, // member signed in
  gacl?: string, // commerce click location
  gatt?: string, // tracking tree
  bsid?: string, // booking session id
};

/**
 * The internal flow type for a /GARecord AJAX call.
 */
type GARecordAJAXObj = GARecordObj & {
  gass?: string,
  gacl?: string,
};

/**
 * The internal flow type for setting an event cookie.
 */
type GAEventCookieObj = GARecordObj & {
  gass: string,
  gads: string,
  gadl?: string,
};

/**
 * Create a GARecordAJAXObj from TrackingEventAJAXProps. This provides reasonable defaults and strips out undefined
 * values.
 *
 * Exported for testing purposes, probably not something you want to interact with directly. Use trackEventAjax()
 * instead.
 */
export const createGARecordAJAXObj = (props: TrackingEventAJAXProps): GARecordAJAXObj => {
  let { value } = props;
  if (!value) {
    value = 0;
  } else if (typeof value !== 'number') {
    value = parseFloat(value);
    if (Number.isNaN(value)) {
      value = 0;
    }
  }

  let { nonInteraction } = props;
  if (typeof nonInteraction !== 'boolean') {
    nonInteraction = nonInteraction === true;
  }

  let { srcServlet } = props;
  /* eslint-disable no-restricted-globals */
  if (srcServlet) {
    // trim any leading slash
    if (srcServlet.indexOf('/') === 0) {
      srcServlet = srcServlet.substring(1);
    }
  } else if (!srcServlet && window && window.location && window.location.pathname) {
    const matches = /\/([^-]*)/.exec(window.location.pathname);
    srcServlet = matches ? matches[1] : null;
  }
  /* eslint-enable no-restricted-globals */
  if (srcServlet === null) {
    srcServlet = undefined;
  }

  let { location } = props;
  if (!location) {
    // eslint-disable-next-line no-restricted-globals
    location = document && document.location ? getLocationFromTAUrl(document.location.href) : null;
  }
  if (location == null) {
    // previous impl. allowed 'null' request param values. Not sure if needed but preserving to avoid any
    // edge case breakage.
    location = 'null';
  }

  let { isMemberSignedIn } = props;
  if (isMemberSignedIn === null || typeof isMemberSignedIn === 'undefined') {
    isMemberSignedIn = false;
  }

  let { bookingSessionId } = props;
  if (typeof bookingSessionId === 'undefined') {
    bookingSessionId = null;
  }

  let { context } = props;
  if (typeof context === 'undefined') {
    // previous impl. would send the string literal 'undefined' over the wire if no context (what GARecord calls a
    // 'label' or 'gal') was provided.
    context = 'undefined';
  }

  const data = {
    gac: props.module,
    gaa: props.action,
    gal: context,
    gav: String(value),
    gani: String(nonInteraction),
    gass: srcServlet,
    gasl: location,
    gapu: props.parentUid,
    gams: isMemberSignedIn ? '1' : '0',
    gacl: props.commerceClickLocation,
    gatt: props.trackingTree,
    bsid: props.bookingSessionId,
  };

  // remove undefined values
  Object.keys(data)
    .filter(key => typeof data[key] === 'undefined')
    .forEach(key => delete data[key]);

  return data;
};

/**
 * Wrap a function and extend the params with additional tracking params pulled from the redux store.
 */
const extendWithReduxProps = (props: $Shape<TrackingEventAJAXProps>): TrackingEventAJAXProps => {
  const state = getStore().getState();
  return {
    srcServlet: state.tracking.analytics ? state.tracking.analytics.url : undefined,
    location: state.page.detailId || state.page.geoId,
    parentUid: state.tracking.uid ? state.tracking.uid : undefined,
    isMemberSignedIn: state.auth.isMember,
    ...props, // props needs to go at the end since callers can override values if provided
  };
};

/**
 * Records an event on the page, ensuring that a given module, action, label, value combo
 * is only tracked once
 */
const trackedPageEvents = {};
export const trackEventAJAXOnPageOnce = (props: TrackingEventAJAXProps) => {
  const eventKey: string = `${props.module || ''}|${props.action || ''}|${props.context ||
    ''}|${props.value || ''}`;
  if (trackedPageEvents[eventKey]) {
    return;
  }
  trackedPageEvents[eventKey] = true;

  const gaRecordObj = createGARecordAJAXObj(extendWithReduxProps(props));
  const searchParams = new URLSearchParams(gaRecordObj);

  enqueue(searchParams);
};

/**
 * Track an event via AJAX. Note that if you are trying to track something as you are unloading the page then
 * you should be using 'trackEventCookie()' instead to avoid a race condition.
 */
const trackEventAJAX = (props: TrackingEventAJAXProps) => {
  const gaRecordObj = createGARecordAJAXObj(extendWithReduxProps(props));
  const searchParams = new URLSearchParams(gaRecordObj);

  enqueue(searchParams);

  /* eslint-enable no-restricted-globals */
};
export default trackEventAJAX;

/**
 * Create a GAEventCookieObj from TrackingEventCookieProps. This provides reasonable defaults and strips out undefined
 * values
 *
 * Exported for testing purposes, probably not something you want to interact with directly. Use trackEventWithCookie()
 * instead.
 */
export const createGARecordCookieObj = (props: TrackingEventCookieProps): GAEventCookieObj => {
  const gaRecordObj = createGARecordAJAXObj(extendWithReduxProps(props));

  let destinationServlet;
  let destinationLocation;
  const { destinationUrl } = props;

  // Allow for override of dstServlet, otherwise grab destination servlet from URL.
  if (props.dstServlet) {
    destinationServlet = props.dstServlet;
  } else if (destinationUrl) {
    destinationServlet = getServletFromTAUrl(destinationUrl) || undefined;
    destinationLocation = getLocationFromTAUrl(destinationUrl) || '';
  }

  // for the AJAX path an empty source location will be the 'null' string but for a cookie it is
  // a blank string. Don't know if this is necessary but is it keeping with prior behavior.
  let { gasl } = gaRecordObj;
  if (gasl && gasl === 'null') {
    gasl = '';
  }

  const data = {
    ...gaRecordObj,
    gads: destinationServlet || props.dstServlet,
    gadl: destinationLocation,
    gasl,
  };

  // remove undefined values
  Object.keys(data)
    .filter(key => typeof data[key] === 'undefined')
    .forEach(key => delete data[key]);

  return data;
};

/**
 * Track an event via a short lived cookie that will get picked up by the next request to the server. This
 * is used when we want to track something as we are leaving the page.
 *
 *
 * If cookieDomain is not included it will pull it from the Redux store
 */
export const trackEventCookie = (
  props: TrackingEventCookieProps,
  srcElement?: HTMLElement,
  cookieDomain?: string,
) => {
  let domain = cookieDomain;
  if (!domain) {
    const state = getStore().getState();
    domain = state.meta.cookieDomain;
  }

  // Note that the source servlet ('gass') and destination servlet ('gads') are required and that is enforced
  // by flow
  const gaRecordObj = createGARecordCookieObj(props);

  // Without valid source and destination servlets, EVT cookie doesn't work. In theory flow guarantees this but
  // just to be safe
  const { gass, gac = 'null', gaa = 'null', gal = 'null' } = gaRecordObj;
  if (!gass) {
    throw new Error(`Src servlet undefined, gac:${gac}, gaa:${gaa}, gal:${gal}`);
  }
  if (!gaRecordObj.gads) {
    throw new Error(`Dst servlet undefined, gac:${gac}, gaa:${gaa}, gal:${gal}`);
  }

  // Write out a cookie that expires in 5 seconds, so that it gets submitted on the next request
  const date = new Date();
  date.setTime(date.getTime() + 5000);
  const gaRecordStr = encodeURIComponent(encodeDotStar(gaRecordObj));
  const cookieVal = `${gaRecordStr}; domain=${domain}; path=/; expires=${date.toUTCString()}`;

  /* eslint-disable no-restricted-globals */
  document.cookie = `EVT=${cookieVal}`;

  /* eslint-enable no-restricted-globals */
};

/**
 * Appends a raw javascript object to the MEVT cookie for logging on
 * the server during the next page load. This will handle escaping
 * the values but will *NOT* add, modify, or validate any of the
 * values. The caller is responsible for assembling all the required
 * fields and validating them. Chances are you will want to use a
 * helper function rather than invoking this directly.
 *
 * If you are adding a new tracking type to the MEVT cookie please
 * seriously consider writing a helper function that calls this
 *
 * @return boolean true is the value was successfully added, false
 * if it was not. This could be caused by an exception or the cookie
 * length could have gotten too large. If the event is truly
 * important it may be worth checking for a false return in the
 * helper functions that call this so blocking ajax calls can be
 * fired off as a fallback. As it currently stands, no fallbacks are
 * in place.
 */
export const addMevtCookie = (params: { [string]: any }, cookieDomain?: string) => {
  let domain = cookieDomain;
  if (!domain) {
    const state = getStore().getState();
    domain = state.meta.cookieDomain;
  }

  let mevtCookie;
  try {
    mevtCookie = Cookies.get('MEVT') || '';
    if (mevtCookie.length > 0) {
      mevtCookie += '~'; // Separate old values with an array separator tilde
    }
    mevtCookie += encodeDotStar(params);
    if (mevtCookie.length > 1024) {
      // We are tracking too many events in the MEVT
      // cookie. Expanding the cookies to too large a size will
      // prevent users from accessing the site
      // https://twiki.dev.tripadvisor.com/bin/view/DesktopTeam/CookieSizeProblem
      // . Better to drop the event than to break everything.
      throw new Error('MEVT Cookie is full');
    }

    // Write out a cookie that expires in 5 seconds, so that it gets submitted on the next request
    const date = new Date();
    date.setTime(date.getTime() + 5000);
    const cookieVal = `${encodeURIComponent(
      mevtCookie,
    )}; domain=${domain}; path=/; expires=${date.toUTCString()}`;

    // eslint-disable-next-line no-restricted-globals
    document.cookie = `MEVT=${cookieVal}`;
  } catch (e) {
    // TODO: errorRecord(e, '_appendMevtCookie', null, arguments);
    return false;
  }
  return true;
};

export const trackClick = (evt: SyntheticEvent<*>, trackingProps: TrackingEventAJAXProps) => {
  const eventProps = {
    ...trackingProps,
    once: undefined,
    children: undefined,
  };

  // If we clicked a TripAdvisor link then we should use an event cookie to avoid a race condition between
  // an AJAX call and the page unloading. Note that this means if we are going to a non-TA page we will
  // still have a race condition since we don't have another option (except blocking unload which is bad).
  const { target, currentTarget } = evt;
  if (target && currentTarget && target instanceof HTMLElement) {
    let currentNode = (target: HTMLElement);
    do {
      if (currentNode instanceof HTMLElement && currentNode.dataset.encodedUrl) {
        // convert element w/ data-encodedUrl to anchor tag, then let
        // the next block handle it.
        const decodedUrl = decodeString(currentNode.dataset.encodedUrl);
        // eslint-disable-next-line no-restricted-globals
        const newAnchor = document.createElement('a');
        newAnchor.href = decodedUrl;
        if (currentNode.parentElement instanceof HTMLElement) {
          newAnchor.style.display = 'none';
          currentNode.parentElement.appendChild(newAnchor);
        }
        currentNode = newAnchor;
      }

      if (currentNode instanceof HTMLAnchorElement) {
        const anchor = (currentNode: HTMLAnchorElement);
        /* eslint-disable no-restricted-globals */
        if (
          window &&
          window.location &&
          window.location.hostname &&
          window.location.hostname === anchor.hostname
        ) {
          /* eslint-enable no-restricted-globals */
          const destinationUrl = anchor.href;
          const cookieProps = {
            ...eventProps,
            destinationUrl, // destination URL is required for setting an event cookie
          };
          trackEventCookie(cookieProps, target);
          return;
        }
      }

      currentNode = currentNode.parentElement;
    } while (
      // end once we reach the root of this component where the event handler is attached
      currentNode &&
      currentNode !== currentTarget
    );
  }

  // otherwise we fall back to using an AJAX call.
  trackEventAJAX(eventProps);
};
